<template>
    <div v-if="isQuestsDataLoaded" class="bonusomat-quests__hidden-card-titles">
        <div v-for="quest in questsData" :key="quest.id" ref="elemQuestCardTitles" v-html="quest.title"></div>
    </div>
    <section class="bonusomat-quests" :style="`--minTitleHeight: ${maxTitleHeightComp}px`">
        <TypographyHeadline theme="dark">
            Questy
            <DsBadge :text="questsCount.toString()" class="bonusomat-quests__quests-count" />
            <DsButton
                type="text"
                theme="dark"
                icon-right="help"
                @click="openBonusomatOnboardingClick"
                class="button-onboarding"
            />
            <div class="bonusomat-quests__arrows" v-if="areArrowsVisible">
                <DsButton
                    theme="dark"
                    type="interaction"
                    size="sm"
                    icon-right="arrow-left"
                    @click="prevSlide"
                    :is-disabled="isLeftButtonDisabled"
                />
                <DsButton
                    theme="dark"
                    type="interaction"
                    size="sm"
                    icon-right="arrow-right"
                    @click="nextSlide"
                    :is-disabled="isRightButtonDisabled"
                />
            </div>
        </TypographyHeadline>
        <template v-if="isActiveQuestVisible && isBreakpointUpMd">
            <h3 class="bonusomat-quests__title">Aktivní</h3>
            <BonusomatQuestCard
                :quest="activeQuest"
                :progress-load-counter="'activeQuest-' + questCardProgressLoadCounter.toString()"
            />
            <h3 class="bonusomat-quests__title bonusomat-quests__title--arrows" v-if="questsGrouped.length">
                Další Questy
                <div class="bonusomat-quests__arrows">
                    <DsButton
                        theme="dark"
                        type="interaction"
                        size="sm"
                        icon-right="arrow-left"
                        @click="prevSlide"
                        :is-disabled="isLeftButtonDisabled"
                    />
                    <DsButton
                        theme="dark"
                        type="interaction"
                        size="sm"
                        icon-right="arrow-right"
                        @click="nextSlide"
                        :is-disabled="isRightButtonDisabled"
                    />
                </div>
            </h3>
        </template>
        <div
            class="bonusomat-placeholder bonusomat-placeholder--quests bonusomat-quests-carousel"
            v-else-if="!isQuestsDataLoaded || !isPlayerDataLoaded"
        >
            <div
                v-for="index in 8"
                :key="index"
                class="bonusomat-placeholder__card bonusomat-quest-card bonusomat-placeholder__skeleton"
                :class="index > 4 ? 'bonusomat-placeholder__card--hidden-on-desktop' : ''"
                :style="{
                    animationDelay: `${index * 0.1}s`,
                }"
            ></div>
        </div>
        <div
            class="bonusomat-placeholder bonusomat-placeholder--quests bonusomat-quests-carousel"
            v-else-if="!questsCount && isQuestsDataLoaded && isPlayerDataLoaded"
        >
            <div
                v-for="index in 8"
                :key="index"
                class="bonusomat-placeholder__card bonusomat-quest-card"
                :class="index > 4 ? 'bonusomat-placeholder__card--hidden-on-desktop' : ''"
            ></div>
            <div class="bonusomat-placeholder__absolute">
                <div class="bonusomat-placeholder__text" v-if="!questsDataHasError" v-html="noQuestMessage"></div>
                <template v-else>
                    <div class="bonusomat-placeholder__text">Při načítání Questů nastala chyba</div>
                    <div class="bonusomat-placeholder__subtext">Zkuste to prosím později</div>
                </template>
            </div>
        </div>
        <div
            class="bonusomat-quests-carousel__wrapper"
            v-show="questsCount && isQuestsDataLoaded && isPlayerDataLoaded"
        >
            <div class="bonusomat-quests-carousel" ref="container">
                <div
                    class="bonusomat-quests-carousel__item"
                    v-for="(questGroup, groupIndex) in questsGrouped"
                    :key="groupIndex"
                    :class="{
                        'bonusomat-quests-carousel__item--column': isBreakpointUpMd && !isActiveQuestVisible,
                        'bonusomat-quests-carousel__item--active':
                            !isBreakpointUpMd && hasActiveQuest && groupIndex == 0,
                    }"
                >
                    <BonusomatQuestCard
                        v-for="quest in questGroup"
                        :key="quest.id"
                        :quest="quest"
                        :progress-load-counter="
                            !isBreakpointUpMd && hasActiveQuest && groupIndex == 0
                                ? 'activeQuestSmall-' + questCardProgressLoadCounter.toString()
                                : undefined
                        "
                        :use-calculated-title-height="isBreakpointUpMd && isQuestsDataLoaded && questsCount > 2"
                    />
                </div>
            </div>
        </div>

        <QuestDetail v-if="isQuestModalOpen" />
        <ConfirmModal />
    </section>
</template>

<script lang="ts" setup>
import { ref, computed, watch, nextTick } from "vue";
import { useKeenSlider } from "keen-slider/vue.es";

import TypographyHeadline from "@sazka/design-system/src/typography/typography-headline.vue";
import DsButton from "@sazka/design-system/src/button/ds-button.vue";
import DsBadge from "@sazka/design-system/src/badge/ds-badge.vue";
import { BonusomatDatalayer } from "@/casino/bonusomat/utils/analytics/bonusomat-datalayer";
import QuestDetail from "./bonusomat-quest-detail.vue";
import BonusomatQuestCard from "./bonusomat-quest-card.vue";
import ConfirmModal from "./bonusomat-quests-confirm-modal.vue";
import useBonusomatData from "../../composables/use-bonusomat-data";
import useBonusomatOnboarding from "../../composables/use-bonusomat-onboarding";
import { useBreakpoints } from "@sazka/utils/composables/use-breakpoints";
import { useLoyaltyPlayerStatus } from "@/loyalty/composables/use-loyalty-player-status";

const { bonusomatPlayerStatus } = useLoyaltyPlayerStatus();
const { bonusomatPlayerData, remainingSpinsCount, isPlayerDataLoaded } = bonusomatPlayerStatus;

const {
    hasActiveQuest,
    activeQuest,
    questsData,
    isQuestsDataLoaded,
    questCardProgressLoadCounter,
    questsDataHasError,
    isQuestModalOpen,
} = useBonusomatData();

const { openBonusomatOnboarding } = useBonusomatOnboarding();

const isBreakpointUpMd = useBreakpoints("md");
const isBreakpointUpLg = useBreakpoints("lg");

const elemQuestCardTitles = ref([]);
const maxTitleHeightComp = computed(() => {
    const titleLineHeight = isBreakpointUpLg.value ? 24 : 20;

    if (isQuestsDataLoaded.value) {
        return Math.max.apply(
            titleLineHeight,
            elemQuestCardTitles.value.map((el) => (el as HTMLElement)?.clientHeight ?? titleLineHeight)
        );
    }

    return titleLineHeight;
});

const noQuestMessage = "Na nový Quest se můžete těšit každou středu a neděli vždy od&nbsp;12:00";

const isActiveQuestVisible = computed(() => hasActiveQuest.value && activeQuest.value);
const questsCount = computed(() => questsData.value?.length ?? 0);

const questsGrouped = computed(() => {
    if (!questsData.value?.length) return [];

    const sortedQuests = [...questsData.value].sort((questA, questB) => {
        if (questA.id == bonusomatPlayerData.value.activeQuestId) return -1;
        if (questB.id == bonusomatPlayerData.value.activeQuestId) return 1;

        if (questA.completed && !questB.completed) return 1;
        if (!questA.completed && questB.completed) return -1;

        const durationA = new Date(questA.endDate).getTime() - new Date(questA.startDate).getTime();
        const durationB = new Date(questB.endDate).getTime() - new Date(questB.startDate).getTime();

        return durationA - durationB;
    });

    if (isBreakpointUpMd.value && hasActiveQuest.value) {
        sortedQuests.splice(0, 1);
    }

    let groupedQuests = [];

    if (!isBreakpointUpMd.value || hasActiveQuest.value) {
        groupedQuests = sortedQuests.map((quest) => [quest]);
    } else {
        groupedQuests =
            sortedQuests?.reduce((result, _, index, array) => {
                if (index % 2 === 0) {
                    const group = [array[index]];
                    if (array.length > index + 1) group.push(array[index + 1]);

                    result.push(group);
                }
                return result;
            }, []) ?? [];
    }

    return groupedQuests;
});

const openBonusomatOnboardingClick = () => {
    openBonusomatOnboarding("quests");
    BonusomatDatalayer.pushMoreInfoClick("Questy", bonusomatPlayerData.value.points, remainingSpinsCount.value);
};

// CAROUSEL SETUP
const currentSlide = ref(0);
const maxIdx = ref(0);
const [container, slider] = useKeenSlider({
    selector: ".bonusomat-quests-carousel__item",
    slides: {
        perView: "auto",
        spacing: 12,
    },
    breakpoints: {
        "(min-width: 1024px)": {
            slides: {
                perView: "auto",
                spacing: 16,
            },
        },
        "(min-width: 1680px)": {
            slides: {
                perView: "auto",
                spacing: 20,
            },
        },
    },
    slideChanged(s) {
        currentSlide.value = s.track.details?.abs;
    },
    updated(s) {
        currentSlide.value = s.track.details?.abs;
        maxIdx.value = s.track.details?.maxIdx;
    },
    created(s) {
        currentSlide.value = s.track.details?.abs;
        maxIdx.value = s.track.details?.maxIdx;
    },
    optionsChanged(s) {
        currentSlide.value = s.track.details?.abs;
        maxIdx.value = s.track.details?.maxIdx;
    },
});

// CAROUSEL FUNCTIONS
const prevSlide = () => slider.value?.prev();
const nextSlide = () => slider.value?.next();

const isLeftButtonDisabled = computed(() => currentSlide.value === 0 || !questsCount.value);
const isRightButtonDisabled = computed(() => currentSlide.value >= maxIdx.value || !questsCount.value);

watch([activeQuest, questsCount, isBreakpointUpMd], () => {
    nextTick(() => {
        slider.value.update();
        slider.value.track.to(0);
    });
});

// CAROUSEL ARROW STATES
const areArrowsVisible = computed(() => {
    return (
        slider.value &&
        !(isBreakpointUpMd.value && hasActiveQuest.value) &&
        questsCount.value > (isBreakpointUpMd.value ? 2 : 1)
    );
});
</script>
